import { AlertHexagon, Mail01, Paperclip, SearchLg } from "@untitledui/icons";
import { useLocation, useNavigate } from "react-router";
import { Badge } from "@/components/base/badges/badges";
import { Input } from "@/components/base/input/input";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/hooks/use-i18n";
import { navigationStateFrom } from "@/types/navigation";
import { ContainerRouteProgress } from "@/components/application/containers/container-route-progress";
import { getRiskLevelColor } from "./world-map-utils";
import type { WorldMapRoute } from "./world-map-utils";

function getStatusColor(status: string): "gray" | "brand" | "warning" | "success" | "error" {
    switch (status) {
        case "Draft":
            return "gray";
        case "Booked":
        case "In Transit":
            return "brand";
        case "Arrived":
        case "On Hold":
            return "warning";
        case "Delivered":
            return "success";
        case "Cancelled":
            return "error";
        default:
            return "gray";
    }
}

export interface WorldMapListPanelProps {
    isOpen: boolean;
    onClose: () => void;
    searchTerm: string;
    onSearchChange: (value: string) => void;
    routes: WorldMapRoute[];
    progressById: Record<string, number>;
    hoveredContainerId: string | null;
    highlightedRouteId: string | null;
    onContainerClick: (route: WorldMapRoute) => void;
    onContainerHover: (containerId: string | null) => void;
}

export function WorldMapListPanel({
    isOpen,
    onClose,
    searchTerm,
    onSearchChange,
    routes,
    progressById,
    hoveredContainerId,
    highlightedRouteId,
    onContainerClick,
    onContainerHover,
}: WorldMapListPanelProps) {
    const { t } = useTranslation("pages:containers");
    const { translateRiskLevel } = useI18n();
    const location = useLocation();
    const navigate = useNavigate();

    const getIssueCount = (documentStatusOverall?: string, exogenStatusOverall?: string) => {
        let count = 0;
        if (documentStatusOverall && documentStatusOverall !== 'All docs are OK') {
            const docMatch = documentStatusOverall.match(/^(\d+)/);
            if (docMatch) count += parseInt(docMatch[1], 10);
        }
        if (exogenStatusOverall && exogenStatusOverall !== 'All systems OK') {
            const issueMatch = exogenStatusOverall.match(/^(\d+)/);
            if (issueMatch) count += parseInt(issueMatch[1], 10);
        }
        return count;
    };

    return (
        <div
            className={`absolute inset-y-0 left-0 z-[1000] bg-primary_alt shadow-xl transition-all duration-300 overflow-hidden flex flex-col ${isOpen ? 'w-[400px]' : 'w-0'}`}
        >
            {isOpen && (
                <div className="flex flex-col h-full w-full">
                    <div className="relative px-4 pt-6 pb-4 border-b border-secondary_alt flex-shrink-0 space-y-3">
                        <div>
                            <h3 className="text-lg font-medium text-brand-primary">{t('worldMap.containerList')}</h3>
                            <p className="mt-0.5 text-xs text-tertiary">
                                {routes.length} {routes.length === 1 ? t('worldMap.containerSingular') : t('worldMap.containerPlural')}
                            </p>
                        </div>
                        <Input
                            placeholder={t('worldMap.searchPlaceholder', { defaultValue: 'Search container, carrier...' })}
                            value={searchTerm}
                            onChange={onSearchChange}
                            icon={SearchLg}
                            size="sm"
                            className="w-full"
                        />
                        <button
                            onClick={onClose}
                            className="absolute top-3 right-3 text-tertiary hover:text-primary transition-colors"
                            type="button"
                        >
                            <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-3 md:px-4 pb-6">
                        {routes.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 px-4">
                                <div className="rounded-full bg-secondary p-4 mb-4">
                                    <svg className="size-10 text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                    </svg>
                                </div>
                                <h4 className="text-md font-semibold text-primary mb-1">{t('worldMap.emptyState.title', 'Nessun container con posizione')}</h4>
                                <p className="text-sm text-tertiary text-center max-w-xs">
                                    {t('worldMap.emptyState.description', 'I container in questo periodo non hanno ancora dati di tracciamento disponibili o non sono ancora partiti')}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3 pt-4">
                                {routes.map((route) => {
                                    const { c, color } = route;
                                    const progress = Math.round((progressById[c.id] ?? 0) * 100);
                                    const isHovered = hoveredContainerId === c.id;
                                    const isSelected = highlightedRouteId === c.id;
                                    const emailCount = c.emailCount ?? 0;
                                    const attachmentsCount = c.documentCount ?? 0;
                                    const issueCount = getIssueCount(c.documentStatusOverall, c.exogenStatusOverall);
                                    

                                    return (
                                        <div
                                            key={c.id}
                                            role="button"
                                            tabIndex={0}
                                            className={`rounded-xl bg-primary p-3.5 cursor-pointer transition-shadow ${isHovered || isSelected ? 'shadow-md' : 'hover:shadow-sm'}`}
                                            onMouseEnter={() => onContainerHover(c.id)}
                                            onMouseLeave={() => onContainerHover(null)}
                                            onClick={() => onContainerClick(route)}
                                            onKeyDown={(e) => e.key === 'Enter' && onContainerClick(route)}
                                        >
                                            <div className="space-y-2.5">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <h4 className="truncate text-2sm font-semibold text-primary">{c.containerNumber}</h4>
                                                        <p className="mt-0.5 text-xs leading-4 text-tertiary">{c.route.from} → {c.route.to}</p>
                                                        <p className="mt-0.5 truncate text-xs text-quaternary">{c.shippingCompany ?? "—"}</p>
                                                    </div>

                                                    <div
                                                        className="flex items-center gap-2 text-xs text-tertiary"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <button
                                                            type="button"
                                                            className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 hover:bg-secondary_alt hover:text-primary transition-colors"
                                                            title={t("list.expandedDetails.viewEmails")}
                                                            onClick={() => {
                                                                const params = new URLSearchParams();
                                                                params.set('category', 'email');
                                                                params.set('container', c.containerNumber || '');
                                                                navigate(`/shipper/activity?${params.toString()}`, { state: navigationStateFrom(location) });
                                                            }}
                                                        >
                                                            <Mail01 className="size-3.5" />
                                                            <span>{emailCount}</span>
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 hover:bg-secondary_alt hover:text-primary transition-colors"
                                                            title={t("list.expandedDetails.viewDocuments")}
                                                            onClick={() => {
                                                                navigate(`/shipper/containers/${c.id}?view=spreadsheet&tab=documents`, { state: navigationStateFrom(location) });
                                                            }}
                                                        >
                                                            <Paperclip className="size-3.5" />
                                                            <span>{attachmentsCount}</span>
                                                        </button>

                                                        <div className={`inline-flex items-center gap-1 rounded-md px-1.5 py-1 ${issueCount > 0 ? 'text-warning-500' : 'text-tertiary'}`}>
                                                            <AlertHexagon className="size-3.5" />
                                                            <span>{issueCount}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center justify-between gap-2">
                                                    <Badge color={getRiskLevelColor(c.riskLevel)} size="sm">
                                                        {translateRiskLevel(c.riskLevel)}
                                                    </Badge>
                                                    <div className="flex items-center gap-2">
                                                        <Badge color={getStatusColor(c.status)} size="sm">
                                                            {c.status}
                                                        </Badge>
                                                        <span className="text-xs font-medium text-primary">{progress}%</span>
                                                    </div>
                                                </div>

                                                <ContainerRouteProgress
                                                    container={c}
                                                    routeColor={color}
                                                    progressOverride={progressById[c.id]}
                                                    shipClampMinPct={18}
                                                    shipClampMaxPct={78}
                                                    clampPortNames={false}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
